import React from "react";
import { Row, Col } from "react-bootstrap";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const amazonUrl = "https://ll-qa.s3.amazonaws.com/media/aereo";

const AereoSummary = ({ envisionData }) => {
  if (!envisionData) return null;

  const segments = envisionData.choosenFlight?.segments || {};
  const airportsMap = segments.airports || {};
  const passengers = envisionData.choosenFlight?.passengers || [];

  const passengersCount = (() => {
    if (Array.isArray(passengers) && passengers.length > 0) {
      return passengers.length;
    }

    if (Array.isArray(segments.fares)) {
      return segments.fares.reduce(
        (sum, fare) => sum + (Number(fare.Quantity) || 0),
        0,
      );
    }

    return 0;
  })();

  const cabinLabelByKey = {
    Economy: "Econômica",
    PremiumEconomy: "Econômica Premium",
    Business: "Executiva",
    Executive: "Executiva",
    First: "Primeira Classe",
  };

  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return "";
    return format(date, "dd MMM yyyy", { locale: ptBR });
  };

  const getStopsLabel = (stops) => {
    const n = Number(stops);
    if (!Number.isFinite(n) || n <= 0) return "Direto";
    return `${n} conex${n > 1 ? "ões" : "ão"}`;
  };

  const makeAirport = (code) => {
    const a = airportsMap?.[code] || {};
    return { code: a.Code || code, cityName: a.CityName || "" };
  };

  const toSegment = (container) => {
    const flights = container?.Flights || [];
    if (!Array.isArray(flights) || flights.length === 0) return null;

    const firstFlight = flights[0] || {};
    const lastFlight = flights[flights.length - 1] || firstFlight;

    const departureAirport = makeAirport(container.DepartureStation);
    const destinationAirport = makeAirport(container.ArrivalStation);

    return {
      departureCode: container.DepartureStation,
      arrivalCode: container.ArrivalStation,
      departureDate: container.DepartureDateTime,
      arrivalDate: container.ArrivalDateTime,
      company: segments.validatingCarrier || container.ValidatingCarrier || firstFlight?.CarrierCode || "",
      stops: flights.length - 1,
      flights: flights.map((f) => ({
        departureDate: f.DepartureDateTime,
        arrivalDate: f.ArrivalDateTime,
        carrierCode: f.CarrierCode,
        cabin: f.FareOption?.Cabin,
        departureAirport: makeAirport(f.DepartureStation),
        arrivalAirport: makeAirport(f.ArrivalStation),
      })),
      departureAirport,
      destinationAirport,
    };
  };

  const departureLegs = (() => {
    const dep = toSegment(segments.departureFlights);
    return dep ? [dep] : [];
  })();

  const returnLegs = (() => {
    const ret = toSegment(segments.returnFlights);
    return ret ? [ret] : [];
  })();

  const getLegInfo = (segment) => {
    const flights = segment?.flights || [];
    const firstFlight = flights[0] || {};
    const lastFlight = flights[flights.length - 1] || firstFlight;

    const originAirport = firstFlight.departureAirport || segment?.departureAirport || {};
    const destinationAirport = lastFlight.arrivalAirport || segment?.destinationAirport || {};

    const originCode = originAirport.code || segment?.departureCode || "";
    const destinationCode = destinationAirport.code || segment?.arrivalCode || "";
    const originCity = originAirport.cityName || "";
    const destinationCity = destinationAirport.cityName || "";

    const companyRaw = segment?.company || firstFlight.carrierCode || "";
    const cabinKey = firstFlight.cabin || segment?.cabin || "";

    const routeText = `(${originCode}) ${originCity} → (${destinationCode}) ${destinationCity}`.trim();

    const airlineName = companyRaw;
    const typeIcon = firstFlight.carrierCode;

    return {
      airlineName,
      typeIcon,
      routeText,
      dateText: formatDate(firstFlight.departureDate || segment?.departureDate),
      cabinText: cabinLabelByKey[cabinKey] || cabinKey || "Econômica",
      stopsText: getStopsLabel(segment?.stops),
    };
  };

  return (
    <div id="aereoCard">
      {departureLegs.map((segment, idx) => {
        const leg = getLegInfo(segment);
        const label = idx === 0 ? "Ida" : `Ida ${idx + 1}`;
        return (
          <Row key={`ida-${idx}`} className="mb-4">
            <Col>
              <div className="d-flex">
                <div
                  className="d-flex align-items-center"
                  style={{ minWidth: 64 }}
                >
                  <div className="d-flex align-items-center">
                    <img
                      src={`${amazonUrl}/${leg.typeIcon}.png`}
                      alt={leg.airlineName}
                      style={{ width: 32, objectFit: "contain" }}
                    />
                  </div>
                </div>
                <div className="flex-grow-1 overflow-hidden">
                  <p className="font-wl-600 text-sm color-text-dark mb-1">
                    {label} • {leg.dateText}
                  </p>
                  <p className="text-sm text-muted text-truncate mb-0">
                    {leg.routeText}
                  </p>
                  <p className="text-sm text-muted text-truncate mb-0">
                    {leg.stopsText} • {leg.cabinText} • {passengersCount} passageiro
                    {passengersCount === 1 ? "" : "s"}
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        );
      })}

      {returnLegs.map((segment, idx) => {
        const leg = getLegInfo(segment);
        const label = idx === 0 ? "Volta" : `Volta ${idx + 1}`;
        return (
          <Row key={`volta-${idx}`} className="mb-4">
            <Col>
              <div className="d-flex">
                <div
                  className="d-flex align-items-center"
                  style={{ minWidth: 64 }}
                >
                  <div className="d-flex align-items-center">
                    <img
                      src={`${amazonUrl}/${leg.typeIcon}.png`}
                      alt={leg.airlineName}
                      style={{ width: 32, objectFit: "contain" }}
                    />
                  </div>
                </div>
                <div className="flex-grow-1 overflow-hidden">
                  <p className="font-wl-600 text-sm color-text-dark mb-1">
                    {label} • {leg.dateText}
                  </p>
                  <p className="text-sm text-muted text-truncate mb-0">
                    {leg.routeText}
                  </p>
                  <p className="text-sm text-muted text-truncate mb-0">
                    {leg.stopsText} • {leg.cabinText} • {passengersCount} passageiro
                    {passengersCount === 1 ? "" : "s"}
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        );
      })}
    </div>
  );
};

AereoSummary.propTypes = {
  envisionData: PropTypes.shape({
    choosenFlight: PropTypes.shape({
      segments: PropTypes.shape({
        sourceCode: PropTypes.string,
        validatingCarrier: PropTypes.string,
        fares: PropTypes.arrayOf(PropTypes.object),
        departureFlights: PropTypes.object,
        returnFlights: PropTypes.object,
        airports: PropTypes.object,
      }),
      passengers: PropTypes.arrayOf(PropTypes.object),
    }),
    segments: PropTypes.shape({
      sourceCode: PropTypes.string,
      validatingCarrier: PropTypes.string,
      fares: PropTypes.arrayOf(PropTypes.object),
      departureFlights: PropTypes.object,
      returnFlights: PropTypes.object,
      airports: PropTypes.object,
    }),
    passengers: PropTypes.arrayOf(PropTypes.object),
    travellers: PropTypes.arrayOf(PropTypes.object),
  }),
};

export default AereoSummary;
